import { createPortal } from 'react-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  align?: 'left' | 'right';
  className?: string;
}

interface MenuPos {
  top: number;
  left?: number;
  right?: number;
}

export function Dropdown({ trigger, children, align = 'right', className }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos>({ top: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const inTrigger = wrapRef.current?.contains(e.target as Node);
      const inMenu = menuRef.current?.contains(e.target as Node);
      if (!inTrigger && !inMenu) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleToggle = () => {
    if (!wrapRef.current) { setOpen((o) => !o); return; }
    const rect = wrapRef.current.getBoundingClientRect();
    const top = rect.bottom + 8;
    if (align === 'right') {
      setPos({ top, right: window.innerWidth - rect.right });
    } else {
      setPos({ top, left: rect.left });
    }
    setOpen((o) => !o);
  };

  return (
    <div ref={wrapRef} className={cn('relative inline-block', className)}>
      <div onClick={handleToggle}>{trigger}</div>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            right: pos.right,
            zIndex: 9999,
          }}
          className="min-w-[12rem] border border-[#E3E3F1] rounded-lg bg-white shadow-[0_8px_20px_rgba(15,23,42,0.10)] py-1"
          role="menu"
        >
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>,
        document.body,
      )}
    </div>
  );
}

export function DropdownItem({
  onClick,
  children,
  danger,
  disabled,
}: {
  onClick?: () => void;
  children: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full text-left px-3 py-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        danger
          ? 'text-[#B91C1C] hover:bg-[#FEF2F2]'
          : 'text-[#12122C] hover:bg-[#EFF0FF] hover:text-[#0410BD]',
      )}
    >
      {children}
    </button>
  );
}
